<template>
  <div class="login">
    <div class="form container">
      <div class="row">
        <div class="col-xs-12 margin-bottom-form-field" v-if="loginWithPhone">
          <div :class="`phone-field ${errors.username.length ? 'error' : ''}`">
            <div class="country-picker" ref="countryPickerRef" @click.stop>
              <button
                type="button"
                class="country-trigger"
                :aria-expanded="isCountryDropdownOpen"
                @click="toggleCountryDropdown"
              >
                <img
                  class="country-flag"
                  :src="selectedCountryFlag"
                  :alt="selectedCountry.name"
                  width="24"
                  height="18"
                />
                <span class="country-dial">+{{ selectedCountry.dialCode }}</span>
                <span class="country-caret" :class="{ open: isCountryDropdownOpen }"></span>
              </button>
              <div v-if="isCountryDropdownOpen" class="country-dropdown">
                <div class="country-search-wrap">
                  <input
                    ref="countrySearchRef"
                    v-model="countrySearch"
                    class="country-search"
                    type="text"
                    inputmode="search"
                    placeholder="Pretraži državu..."
                    autocomplete="off"
                    @click.stop
                    @keydown.esc.stop="closeCountryDropdown"
                  />
                </div>
                <div class="country-options">
                  <button
                    v-for="country in filteredCountries"
                    :key="country.iso2"
                    type="button"
                    class="country-option"
                    :class="{ active: country.iso2 === selectedCountryIso2 }"
                    @click="selectCountry(country.iso2)"
                  >
                    <img
                      class="country-flag"
                      :src="getCountryFlagUrl(country.iso2)"
                      :alt="country.name"
                      width="24"
                      height="18"
                    />
                    <span class="country-option-name">{{ country.name }}</span>
                    <span class="country-option-dial">+{{ country.dialCode }}</span>
                  </button>
                  <p v-if="!filteredCountries.length" class="country-empty">Nema rezultata</p>
                </div>
              </div>
            </div>
            <input
              v-model="phone"
              class="phone-input"
              type="tel"
              inputmode="numeric"
              autocomplete="tel-national"
              placeholder="641234567"
              @input="onPhoneInput"
            />
          </div>
          <p class="registration-error" v-if="errors.username.length">{{ errors.username }}</p>
          <button type="button" class="alt-method-link" @click="switchToUsername">
            Koristi korisničko ime umesto telefona
          </button>
        </div>
        <div class="col-xs-12 margin-bottom-form-field" v-else>
          <input
            v-model="form.username"
            :class="`tb ${errors.username.length ? 'error' : ''}`"
            type="text"
            placeholder="Korisničko ime ili email"
          />
          <p class="registration-error" v-if="errors.username.length">{{ errors.username }}</p>
          <button type="button" class="alt-method-link" @click="switchToPhone">
            Koristi telefon umesto korisničkog imena
          </button>
        </div>
      </div>
      <div class="row">
        <div class="col-xs-12 margin-bottom-form-field" style="position: relative">
          <input
            v-model="form.password"
            :class="`tb ${errors.password.length ? 'error' : ''}`"
            :type="isVisible ? 'text' : 'password'"
            placeholder="Lozinka"
          />
          <img
            :src="isVisible ? eyeIcons.visible : eyeIcons.hidden"
            class="hidden-eye"
            v-bind:style="changePasswordEyeStyle()"
            alt=""
            @click="handleVisibility"
          />
          <p class="registration-error" v-if="errors.password.length">{{ errors.password }}</p>
        </div>
        <p class="forgot-password" @click="forgotPassword()">Zaboravljena lozinka?</p>
      </div>
    </div>

    <p class="error-text">{{ getLoginErrorMessage }}</p>
    <div class="submit">
      <button class="btnSubmit" :disabled="isLoadingLogin" @click="handleLogin()">
        {{ isLoadingLogin ? "Učitavanje..." : "Ulogujte se" }}
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import store from "@/store";
import {
  buildInternationalPhone,
  DEFAULT_COUNTRY_ISO2,
  findCountryByIso2,
  getCountries,
  getCountryFlagUrl,
  normalizeNationalNumber,
  type CountryDialOption,
} from "@/data/countries";
import { Options, Vue } from "vue-class-component";
import { mapGetters } from "vuex";

@Options({
  computed: {
    ...mapGetters(["getLoginErrorMessage", "isLoadingLogin"]),
  },
})
export default class Login extends Vue {
  loginWithPhone = false;
  phone = "";
  countries: CountryDialOption[] = getCountries();
  selectedCountryIso2 = DEFAULT_COUNTRY_ISO2;
  isCountryDropdownOpen = false;
  countrySearch = "";
  onDocumentClickBound = (event: MouseEvent) => this.handleDocumentClick(event);

  form = {
    username: "",
    password: "",
  };
  errors = {
    username: "",
    password: "",
  };
  eyeIcons = {
    hidden: require("../assets/images/hidden.png"),
    visible: require("../assets/images/show.png"),
  };
  isVisible = false;
  badRequestErrorMessages = {
    username: "Korisničko ime nije odgovarajuće",
    password: "Pogresna lozinka",
  };
  conflictingErrorMessages = {
    username: "Korisnik ne postoji",
    password: "Pogresna lozinka",
  };

  public getLoginErrorMessage: any;
  public isLoadingLogin: any;

  get selectedCountry(): CountryDialOption {
    return findCountryByIso2(this.selectedCountryIso2) || this.countries[0];
  }

  get selectedCountryFlag(): string {
    return getCountryFlagUrl(this.selectedCountry.iso2);
  }

  get filteredCountries(): CountryDialOption[] {
    const query = this.countrySearch.trim().toLowerCase().replace(/^\+/, "");
    if (!query) return this.countries;

    return this.countries.filter((country) => {
      const name = country.name.toLowerCase();
      const dial = country.dialCode.toLowerCase();
      const iso = country.iso2.toLowerCase();
      return name.includes(query) || dial.includes(query) || iso.includes(query) || `+${dial}`.includes(query);
    });
  }

  get fullPhoneNumber(): string {
    return buildInternationalPhone(this.selectedCountry.dialCode, this.phone);
  }

  getCountryFlagUrl = getCountryFlagUrl;

  mounted() {
    document.addEventListener("click", this.onDocumentClickBound);
  }

  beforeUnmount() {
    document.removeEventListener("click", this.onDocumentClickBound);
  }

  handleDocumentClick(event: MouseEvent) {
    if (!this.isCountryDropdownOpen) return;
    const picker = this.$refs.countryPickerRef as HTMLElement | undefined;
    if (picker && !picker.contains(event.target as Node)) {
      this.closeCountryDropdown();
    }
  }

  toggleCountryDropdown() {
    if (this.isCountryDropdownOpen) {
      this.closeCountryDropdown();
      return;
    }
    this.isCountryDropdownOpen = true;
    this.countrySearch = "";
    this.$nextTick(() => {
      const searchInput = this.$refs.countrySearchRef as HTMLInputElement | undefined;
      searchInput?.focus();
    });
  }

  closeCountryDropdown() {
    this.isCountryDropdownOpen = false;
    this.countrySearch = "";
  }

  selectCountry(iso2: string) {
    this.selectedCountryIso2 = iso2;
    this.closeCountryDropdown();
    this.errors.username = "";
  }

  onPhoneInput(event: Event) {
    const target = event.target as HTMLInputElement;
    const digitsOnly = target.value.replace(/\D/g, "");
    this.phone = digitsOnly;
    target.value = digitsOnly;
    this.errors.username = "";
  }

  switchToPhone() {
    this.loginWithPhone = true;
    this.form.username = "";
    this.errors.username = "";
    this.closeCountryDropdown();
    store.commit("SET_LOGIN_ERROR_MESSAGE", "");
  }

  switchToUsername() {
    this.loginWithPhone = false;
    this.phone = "";
    this.errors.username = "";
    this.closeCountryDropdown();
    store.commit("SET_LOGIN_ERROR_MESSAGE", "");
  }

  handleVisibility() {
    this.isVisible = !this.isVisible;
  }

  changePasswordEyeStyle() {
    return this.errors.password === ""
      ? {
          transform: "translateY(-50%)",
        }
      : {
          transform: "translateY(-100%)",
        };
  }

  async handleLogin() {
    if (!this.validateForm()) {
      return;
    }

    this.errors.username = "";
    this.errors.password = "";
    store.commit("SET_LOGIN_ERROR_MESSAGE", "");

    try {
      await store.dispatch("login", {
        username: this.loginWithPhone ? this.fullPhoneNumber : this.form.username,
        password: this.form.password,
      });

      this.form.password = "";
      this.form.username = "";
      this.phone = "";
      this.$router.push("/auth-successful");
    } catch (error: any) {
      if (!("status" in error) || !("data" in error)) return;

      const status = error.status as number;
      const data = error.data as [keyof typeof this.errors];
      for (const key of data) {
        if (status === 400) {
          this.errors[key] = this.badRequestErrorMessages[key];
        } else if (status === 409) {
          this.errors[key] = this.conflictingErrorMessages[key];
        }
      }
    }
  }

  validateForm() {
    this.errors = {
      username: "",
      password: "",
    };

    if (this.loginWithPhone) {
      const national = normalizeNationalNumber(this.phone);
      if (!this.phone.trim()) {
        this.errors.username = "Broj telefona je obavezan";
      } else if (!/^[0-9]{6,12}$/.test(national)) {
        this.errors.username = "Format broja telefona nije odgovarajući";
      }
    } else if (!this.form.username) {
      this.errors.username = "Polje je obavezno";
    }

    if (!this.form.password) this.errors.password = "Lozinka je obavezna";

    const hasErrors = Object.values(this.errors).some((value) => value !== "");
    return !hasErrors;
  }

  forgotPassword() {
    this.$router.push("/forgot-password");
  }
}
</script>
<style scoped>
@media (max-width: 480px) {
  .hidden-eye {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: 30px;
    width: 24px;
  }

  .error-text {
    color: red;
    font-size: 14px;
    margin-top: 0px;
    font-weight: normal;
    text-transform: none;
    text-align: center;
  }

  .forgot-password {
    text-decoration: underline;
    color: white;
    text-align: right;
    margin-right: 15px;
  }

  input {
    color: rgba(156, 156, 156, 1) !important;
    padding-left: 18px;
    font-size: 18px;
  }
  .form {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
  }

  .tb {
    height: 60px;
    width: 326px;
    gap: 0px;
    border-radius: 5px;
    margin: 0 auto;
    background: rgba(35, 35, 35, 1);
  }

  .tb-half {
    height: 60px;
    border-radius: 5px;
    margin: 0 auto;
    width: 150px;
    background: rgba(35, 35, 35, 1);
  }

  .submit {
    display: flex;
    justify-content: center;
    margin-top: 30px;
  }
  .btnSubmit {
    width: 70% !important;
    height: 67px !important;
    padding: 16px 69px 16px 69px !important;
    gap: 8px !important;
    border-radius: 8px !important;
    color: white !important;
    background: rgba(24, 171, 63, 1) !important;
    box-shadow: 0px 9px 16px 0px rgba(214, 255, 200, 0.45) inset !important;
    box-shadow: 0px -6px 10px 0px rgba(0, 98, 3, 0.52) inset !important;
  }

  .btnSubmit:disabled {
    opacity: 0.6 !important;
    cursor: not-allowed !important;
    pointer-events: none !important;
  }

  .margin-bottom-form-field {
    margin-bottom: 10px !important;
  }

  .registration-error {
    color: red !important;
    font-size: 14px !important;
    margin-top: 0px !important;
    font-weight: normal !important;
    text-transform: none !important;
    text-align: left !important;
  }

  .error {
    border: 1px solid red;
  }

  .phone-field {
    width: 326px;
    margin: 0 auto;
    height: 60px;
    border-radius: 5px;
    background: rgba(35, 35, 35, 1);
    display: flex;
    align-items: center;
    position: relative;
  }

  .phone-field.error {
    border: 1px solid red;
  }

  .country-picker {
    position: relative;
    flex-shrink: 0;
    height: 100%;
  }

  .country-trigger {
    height: 100%;
    min-width: 102px;
    padding: 0 10px 0 12px;
    border: none;
    border-right: 1px solid rgba(70, 70, 70, 1);
    background: transparent;
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    color: rgba(200, 200, 200, 1);
    font-family: "Saira", sans-serif;
    font-size: 15px;
  }

  .country-flag {
    width: 24px;
    height: 18px;
    object-fit: cover;
    border-radius: 2px;
    flex-shrink: 0;
  }

  .country-dial {
    color: white;
    font-weight: 600;
  }

  .country-caret {
    width: 0;
    height: 0;
    margin-left: 2px;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 5px solid rgba(156, 156, 156, 1);
    transition: transform 0.15s ease;
  }

  .country-caret.open {
    transform: rotate(180deg);
  }

  .country-dropdown {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    width: 326px;
    background: rgba(35, 35, 35, 1);
    border: 1px solid rgba(70, 70, 70, 1);
    border-radius: 8px;
    z-index: 20;
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.45);
    overflow: hidden;
  }

  .country-search-wrap {
    padding: 10px;
    border-bottom: 1px solid rgba(70, 70, 70, 1);
  }

  .country-search {
    width: 100%;
    height: 40px;
    border-radius: 6px !important;
    border: none !important;
    background: rgba(24, 24, 24, 1) !important;
    color: white !important;
    padding: 0 12px !important;
    font-size: 14px !important;
    font-family: "Saira", sans-serif;
  }

  .country-search::placeholder {
    color: rgba(120, 120, 120, 1);
  }

  .country-options {
    max-height: 200px;
    overflow-y: auto;
  }

  .country-empty {
    margin: 16px 0 !important;
    padding: 0 14px;
    color: rgba(156, 156, 156, 1) !important;
    font-size: 14px !important;
    font-weight: 400 !important;
    text-transform: none !important;
    text-align: center !important;
  }

  .country-option {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    border: none;
    background: transparent;
    color: rgba(200, 200, 200, 1);
    font-family: "Saira", sans-serif;
    font-size: 14px;
    text-align: left;
    cursor: pointer;
  }

  .country-option:hover,
  .country-option.active {
    background: rgba(24, 171, 63, 0.18);
    color: white;
  }

  .country-option-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .country-option-dial {
    color: rgba(156, 156, 156, 1);
    flex-shrink: 0;
  }

  .phone-input {
    flex: 1;
    height: 100%;
    min-width: 0;
    border: none !important;
    background: transparent !important;
    color: rgba(156, 156, 156, 1) !important;
    padding-left: 12px !important;
    padding-right: 12px !important;
    font-size: 18px !important;
    font-family: "Saira", sans-serif;
  }

  .alt-method-link {
    display: block;
    width: 326px;
    margin: 8px auto 0;
    padding: 0;
    border: none;
    background: transparent;
    color: rgba(156, 156, 156, 1);
    font-family: "Saira", sans-serif;
    font-size: 13px;
    font-weight: 400;
    text-align: left;
    text-decoration: underline;
    text-underline-offset: 2px;
    cursor: pointer;
  }
}
</style>
